import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Excalidraw } from '@excalidraw/excalidraw';
import styled from 'styled-components';
import Modal from 'react-modal';
import excalidrawService from '../services/excalidrawService';
import fileService from '../services/fileService';
import { useToast } from '../hooks/use-toast';
import { ExcalidrawErrorBoundary } from './ExcalidrawComponent';

// Set modal app element for accessibility
Modal.setAppElement('#root');

const SketchEditor = ({ isOpen, onClose, noteId, initialSketchId = null }) => {
  // Ref for the Excalidraw component instance
  const excalidrawRef = useRef(null);
  
  // Auto-save related refs and state
  const autoSaveIntervalRef = useRef(null);
  const lastSavedDataRef = useRef(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('saved'); // 'saved', 'saving', 'changes'

  const [excalidrawData, setExcalidrawData] = useState({
    elements: [],
    appState: { viewBackgroundColor: '#FFFFFF', currentItemFontFamily: 1 },
    files: {}
  });
  const [sketchId, setSketchId] = useState(initialSketchId);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  // Load sketch if sketchId is provided
  useEffect(() => {
    if (isOpen && initialSketchId) {
      loadSketch(initialSketchId);
    } else if (isOpen && !initialSketchId) {
      // Reset state for a new sketch when modal opens without initialSketchId
      setExcalidrawData({
        elements: [],
        appState: { viewBackgroundColor: '#FFFFFF', currentItemFontFamily: 1 },
        files: {}
      });
      setSketchId(null);
       // Reset the Excalidraw instance itself if it exists
      if (excalidrawRef.current) {
        excalidrawRef.current.resetScene();
        excalidrawRef.current.updateScene({ appState: { viewBackgroundColor: '#FFFFFF', currentItemFontFamily: 1 } });
      } 
    }
  }, [initialSketchId, isOpen]);

  const loadSketch = async (id) => {
    try {
      setIsLoading(true);
      const response = await excalidrawService.getSketch(id);
      if (response && response.content) {
        // Ensure loaded data has basic structure
        const loadedData = response.content;
        const cleanAppState = loadedData.appState || { viewBackgroundColor: '#FFFFFF', currentItemFontFamily: 1 };
        // Remove potentially problematic state if loading
        delete cleanAppState.collaborators;
        delete cleanAppState.collaboratorCount;
        
        // Process files to convert URLs to blob URLs for Excalidraw
        const processedFiles = {};
        if (loadedData.files) {
          for (const [fileId, fileData] of Object.entries(loadedData.files)) {
            try {
              if (fileData.dataURL && (fileData.dataURL.startsWith('http') || fileData.dataURL.startsWith('/assets/'))) {
                // This is a stored URL, convert it to a blob URL
                const response = await fetch(fileData.dataURL);
                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);
                
                processedFiles[fileId] = {
                  ...fileData,
                  dataURL: blobUrl,
                  originalUrl: fileData.dataURL // Store the original URL
                };
              } else {
                // This is already a blob URL or data URL, keep as is
                processedFiles[fileId] = fileData;
              }
            } catch (error) {
              console.error(`Error loading image ${fileId}:`, error);
              // Skip this file if it can't be loaded
            }
          }
        }
        
        setExcalidrawData({
          elements: loadedData.elements || [],
          appState: cleanAppState,
          files: processedFiles
        });
        setSketchId(response.unique_id);
        
        // Initialize auto-save state for loaded sketch
        lastSavedDataRef.current = {
          elements: loadedData.elements || [],
          files: processedFiles
        };
        setHasUnsavedChanges(false);
        setAutoSaveStatus('saved');
        
        // Update Excalidraw instance after state is set
        if (excalidrawRef.current) {
           excalidrawRef.current.updateScene({ 
              elements: loadedData.elements || [], 
              appState: cleanAppState,
              files: processedFiles
           });
        }
      } else {
        // Handle case where sketch content might be missing
        toast({ title: "Warning", description: "Sketch data loaded but content is missing.", variant: "default" });
        setExcalidrawData({ elements: [], appState: { viewBackgroundColor: '#FFFFFF', currentItemFontFamily: 1 }, files: {} });
        setSketchId(id);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to load sketch: ${error.message}`,
        variant: "destructive"
      });
      // Reset to default state on load error
      setExcalidrawData({ elements: [], appState: { viewBackgroundColor: '#FFFFFF', currentItemFontFamily: 1 }, files: {} });
      setSketchId(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Re-enable state update, focusing on elements and files
  const handleChange = useCallback((elements, appState, files) => {
    // Update state primarily for elements and files
    // We'll get the definitive appState from the ref on save
    setExcalidrawData(prevData => ({
      ...prevData,
      elements: elements || [],
      files: files || {}
      // Avoid setting appState here to prevent potential loops/errors
    }));
    
    // Mark that we have unsaved changes
    setHasUnsavedChanges(true);
    setAutoSaveStatus('changes');
  }, []);

  const saveSketch = async () => {
    if (!excalidrawRef.current) {
      toast({ title: "Error", description: "Sketch component not ready.", variant: "destructive" });
      return;
    }

    try {
      setIsSaving(true);
      
      // Get the latest state directly from the Excalidraw instance
      const currentElements = excalidrawRef.current.getSceneElements();
      const currentAppState = excalidrawRef.current.getAppState();
      const currentFiles = excalidrawRef.current.getFiles();

      // Process files to convert blob URLs back to original URLs for saving
      const processedFiles = {};
      if (currentFiles) {
        Object.entries(currentFiles).forEach(([fileId, fileData]) => {
          if (fileData.originalUrl) {
            // Use the original upload URL instead of the blob URL
            processedFiles[fileId] = {
              ...fileData,
              dataURL: fileData.originalUrl,
              // Remove the blob URL to avoid confusion
              blobUrl: fileData.dataURL
            };
          } else {
            // Keep existing files as they are
            processedFiles[fileId] = fileData;
          }
        });
      }

      const dataToSave = {
        elements: currentElements || [],
        appState: {
          // Save the current app state obtained from the ref
          ...(currentAppState || {}),
          // Ensure viewBackgroundColor is always set, default if needed
          viewBackgroundColor: currentAppState?.viewBackgroundColor || '#FFFFFF',
          // Override or remove any problematic properties if necessary
          collaborators: undefined,
          collaboratorCount: undefined
        },
        files: processedFiles,
      };
      
      let response;

      if (sketchId) {
        // Update existing sketch
        response = await excalidrawService.updateSketch(
          sketchId,
          dataToSave,
          noteId
        );
      } else {
        // Create new sketch
        response = await excalidrawService.saveSketch(
          dataToSave,
          noteId
        );
        setSketchId(response.unique_id); // Update sketchId after creation
      }

      // Update last saved data reference for auto-save comparison
      lastSavedDataRef.current = {
        elements: currentElements || [],
        files: currentFiles || {}
      };
      
      setHasUnsavedChanges(false);
      setAutoSaveStatus('saved');

      toast({
        title: "Success",
        description: sketchId ? "Sketch updated successfully!" : "Sketch saved successfully!"
      });

      return response.unique_id;
    } catch (error) {
      console.error("Error saving sketch:", error);
      toast({
        title: "Error",
        description: `Failed to save sketch: ${error.message}`,
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle image uploads when files are dropped on Excalidraw
  const handleFilesAdded = useCallback(async (files) => {
    if (!files || files.length === 0) return;

    try {
      const processedFiles = {};
      
      for (const file of files) {
        // Validate that it's an image
        if (!file.type.startsWith('image/')) {
          toast({
            title: "Invalid file type",
            description: `${file.name} is not an image file`,
            variant: "destructive"
          });
          continue;
        }

        try {
          // Upload the image using the file service
          const uploadResult = await fileService.uploadFile(file);
          
          if (uploadResult.success && uploadResult.data.uniqueUrl) {
            // Convert the uploaded file to Excalidraw file format
            const fileId = `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            
            // Create a blob URL for the image to be used by Excalidraw
            const response = await fetch(uploadResult.data.uniqueUrl);
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            
            // Create the file object that Excalidraw expects
            processedFiles[fileId] = {
              mimeType: file.type,
              id: fileId,
              dataURL: blobUrl,
              created: Date.now(),
              lastRetrieved: Date.now(),
              // Store the original upload URL for saving
              originalUrl: uploadResult.data.uniqueUrl
            };

            toast({
              title: "Image uploaded",
              description: `${file.name} has been added to your sketch`,
              variant: "default"
            });
          }
        } catch (uploadError) {
          console.error('Error uploading image:', uploadError);
          toast({
            title: "Upload failed",
            description: `Failed to upload ${file.name}: ${uploadError.message}`,
            variant: "destructive"
          });
        }
      }

      // Update the Excalidraw instance with the new files
      if (Object.keys(processedFiles).length > 0 && excalidrawRef.current) {
        const currentFiles = excalidrawRef.current.getFiles();
        const updatedFiles = { ...currentFiles, ...processedFiles };
        
        // Update the local state
        setExcalidrawData(prevData => ({
          ...prevData,
          files: updatedFiles
        }));

        // Update the Excalidraw instance
        excalidrawRef.current.addFiles(Object.values(processedFiles));
      }
    } catch (error) {
      console.error('Error processing files:', error);
      toast({
        title: "Error",
        description: "Failed to process uploaded files",
        variant: "destructive"
      });
    }
  }, [toast]);

  // Handle library changes (when users add items to library)
  const handleLibraryChange = useCallback(async (libraryItems) => {
    // This is called when the library is updated
    // We can save library items if needed, but for now we'll just log
    console.log('Library updated:', libraryItems);
  }, []);

  // Handle when files are dropped directly onto the canvas
  const handleDrop = useCallback(async (event) => {
    event.preventDefault();
    
    const files = Array.from(event.dataTransfer?.files || []);
    if (files.length > 0) {
      await handleFilesAdded(files);
    }
  }, [handleFilesAdded]);

  // initialData should still be derived from state for the initial render
  const initialDataForExcalidraw = useMemo(() => {
    return {
        elements: excalidrawData.elements || [],
        // Make sure appState here is clean too
        appState: {
            ...(excalidrawData.appState || {}),
            viewBackgroundColor: excalidrawData.appState?.viewBackgroundColor || '#FFFFFF',
            currentItemFontFamily: excalidrawData.appState?.currentItemFontFamily || 1,
            collaborators: undefined,
            collaboratorCount: undefined
        },
        files: excalidrawData.files || {}
    };
  }, [excalidrawData]);

  // Auto-save function
  const autoSave = useCallback(async () => {
    if (!excalidrawRef.current || !hasUnsavedChanges || isAutoSaving || isSaving) {
      return;
    }

    try {
      setIsAutoSaving(true);
      setAutoSaveStatus('saving');
      
      // Get the latest state directly from the Excalidraw instance
      const currentElements = excalidrawRef.current.getSceneElements();
      const currentAppState = excalidrawRef.current.getAppState();
      const currentFiles = excalidrawRef.current.getFiles();

      // Check if there are actual changes by comparing with last saved data
      const currentData = {
        elements: currentElements || [],
        appState: currentAppState || {},
        files: currentFiles || {}
      };

      // Simple comparison - if the structure is the same, skip auto-save
      if (lastSavedDataRef.current && 
          JSON.stringify(currentData.elements) === JSON.stringify(lastSavedDataRef.current.elements) &&
          Object.keys(currentData.files).length === Object.keys(lastSavedDataRef.current.files).length) {
        setHasUnsavedChanges(false);
        setAutoSaveStatus('saved');
        return;
      }

      // Process files to convert blob URLs back to original URLs for saving
      const processedFiles = {};
      if (currentFiles) {
        Object.entries(currentFiles).forEach(([fileId, fileData]) => {
          if (fileData.originalUrl) {
            processedFiles[fileId] = {
              ...fileData,
              dataURL: fileData.originalUrl,
              blobUrl: fileData.dataURL
            };
          } else {
            processedFiles[fileId] = fileData;
          }
        });
      }

      const dataToSave = {
        elements: currentElements || [],
        appState: {
          ...(currentAppState || {}),
          viewBackgroundColor: currentAppState?.viewBackgroundColor || '#FFFFFF',
          collaborators: undefined,
          collaboratorCount: undefined
        },
        files: processedFiles,
      };
      
      let response;

      if (sketchId) {
        // Update existing sketch
        response = await excalidrawService.updateSketch(
          sketchId,
          dataToSave,
          noteId
        );
      } else {
        // Create new sketch
        response = await excalidrawService.saveSketch(
          dataToSave,
          noteId
        );
        setSketchId(response.unique_id);
      }

      // Update last saved data reference
      lastSavedDataRef.current = {
        elements: currentElements || [],
        files: currentFiles || {}
      };
      
      setHasUnsavedChanges(false);
      setAutoSaveStatus('saved');

      return response.unique_id;
    } catch (error) {
      console.error("Auto-save error:", error);
      setAutoSaveStatus('changes');
      // Don't show toast for auto-save errors to avoid interrupting user
    } finally {
      setIsAutoSaving(false);
    }
  }, [hasUnsavedChanges, isAutoSaving, isSaving, sketchId, noteId]);

  // Set up auto-save interval
  useEffect(() => {
    if (isOpen) {
      // Start auto-save interval when modal opens
      autoSaveIntervalRef.current = setInterval(autoSave, 5000); // 5 seconds
      
      return () => {
        // Clear interval when modal closes
        if (autoSaveIntervalRef.current) {
          clearInterval(autoSaveIntervalRef.current);
          autoSaveIntervalRef.current = null;
        }
      };
    }
  }, [isOpen, autoSave]);

  // Clean up auto-save interval on unmount
  useEffect(() => {
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
      }
    };
  }, []);

  return (
    <SketchModalContainer
      isOpen={isOpen}
      onRequestClose={() => onClose(sketchId)} // Pass current sketchId on close
      contentLabel="Excalidraw Sketch Editor"
      className="excalidraw-modal"
      overlayClassName="excalidraw-modal-overlay"
    >
      {isLoading ? (
        <LoadingContainer>Loading sketch...</LoadingContainer>
      ) : (
        <>
          <ExcalidrawWrapper>
            <ExcalidrawErrorBoundary>
              <Excalidraw
                // Add the ref here
                ref={excalidrawRef}
                // Key might still be useful for forcing re-render on sketch change
                key={sketchId || 'new-sketch'}
                initialData={initialDataForExcalidraw}
                onChange={handleChange} // Keep this
                onLibraryChange={handleLibraryChange}
                onDrop={handleDrop}
                theme="light"
                UIOptions={{
                  canvasActions: {
                    loadScene: false,
                    saveAsScene: false,
                    saveScene: false,
                  }
                }}
              />
            </ExcalidrawErrorBoundary>
          </ExcalidrawWrapper>
          
          <ButtonsContainer>
            <AutoSaveStatus>
              {autoSaveStatus === 'saving' && (
                <StatusText saving>Auto-saving...</StatusText>
              )}
              {autoSaveStatus === 'changes' && (
                <StatusText changes>Unsaved changes</StatusText>
              )}
              {autoSaveStatus === 'saved' && (
                <StatusText saved>All changes saved</StatusText>
              )}
            </AutoSaveStatus>
            
            <ButtonGroup>
              <Button secondary onClick={() => onClose(sketchId)}> 
                Cancel
              </Button>
              <Button primary onClick={saveSketch} disabled={isSaving || isAutoSaving}>
                {isSaving ? 'Saving...' : 'Save Sketch'}
              </Button>
            </ButtonGroup>
          </ButtonsContainer>
        </>
      )}
    </SketchModalContainer>
  );
};

const SketchModalContainer = styled(Modal)`
  &.excalidraw-modal {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90%;
    height: 90%;
    max-width: 1400px;
    max-height: 900px;
    background-color: white;
    border-radius: 8px;
    padding: 0;
    display: flex;
    flex-direction: column;
    outline: none;
  }
  
  &.excalidraw-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1000;
  }
`;

const ExcalidrawWrapper = styled.div`
  width: 100%;
  height: calc(100% - 60px);
  border-radius: 8px 8px 0 0;
  overflow: hidden;
  .excalidraw {
    height: 100%;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
  border-radius: 0 0 8px 8px;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const Button = styled.button`
  padding: 8px 16px;
  border-radius: 4px;
  font-weight: 500;
  margin-left: 10px;
  cursor: pointer;
  transition: all 0.2s;
  
  ${props => props.primary && `
    background-color: #007bff;
    color: white;
    border: none;
    
    &:hover {
      background-color: #0069d9;
    }
    
    &:disabled {
      background-color: #89bdf7;
      cursor: not-allowed;
    }
  `}
  
  ${props => props.secondary && `
    background-color: #f8f9fa;
    color: #343a40;
    border: 1px solid #ced4da;
    
    &:hover {
      background-color: #e9ecef;
    }
  `}
`;

const AutoSaveStatus = styled.div`
  margin-right: 10px;
`;

const StatusText = styled.span`
  ${props => props.saving && `
    color: #007bff;
  `}
  ${props => props.changes && `
    color: #dc3545;
  `}
  ${props => props.saved && `
    color: #28a745;
  `}
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  font-size: 1.2rem;
  color: #6c757d;
`;

export default SketchEditor; 